"use client";

import React from "react";
import { X } from "lucide-react";
import { cn } from "@aichat/ui/lib/utils";
import { getFooterLink } from "@aichat/constants/utm";
import { AssistantAvatar, Button, Typography } from "@aichat/ui";
import { useWebWidgetConfig } from "../../web-widget/use-web-widget-config";
import { useIsFrameOnMobile } from "@aichat/ui/hooks/use-mobile";

interface ThemeSleek extends Omit<React.HTMLAttributes<HTMLDivElement>, ""> {
  isFirstChat: boolean;
  isVoiceMode: boolean;
  toggleChat: () => void;
  isAlwaysOpen?: boolean;
  preventClose?: boolean;
}

const ThemeSleek: React.FC<ThemeSleek> = ({
  isFirstChat,
  isVoiceMode,
  children,
  toggleChat,
  isAlwaysOpen,
  preventClose,
}) => {
  const isOnMobile = useIsFrameOnMobile();
  const config = useWebWidgetConfig();
  const footerLink = getFooterLink();
  return (
    <>
      <div
        className={cn(
          "pt-[48px] px-2 pb-2 overflow-auto shadow-[-1px_2px_15px_5px_#51515123]",
          isOnMobile
            ? "w-full h-full mb-8"
            : "w-[92.5%] rounded-[36px] h-full mx-[6px] mt-7",
          isOnMobile && isFirstChat && !isVoiceMode
            ? "h-[80%] bottom-0"
            : "h-[96%] bottom-0",
        )}
        id="sleek-chat"
      >
        <div
          id="sleek-chat-bg-blur"
          className={cn(
            "absolute right-0 bottom-0 bg-white/10 backdrop-blur-2xl -z-10",
            isOnMobile
              ? "w-full h-full"
              : "top-[24px] rounded-[36px] mx-[6px] w-[92.5%]",
            isOnMobile && isFirstChat && !isVoiceMode
              ? "h-[84%] rounded-t-[36px] bottom-0"
              : "",
          )}
        />
        {!isOnMobile ? (
          <div
            id="side-link-sleek"
            className="absolute left-[0.25px] bottom-32 w-8 flex justify-center bg-transparent shadow-[-1px_2px_15px_5px_#51515123]"
            style={{
              clipPath: "polygon(0% 10%, 100% 0%, 100% 100%, 0% 90%)",
              backgroundColor: "white",
            }}
          >
            <a
              href={footerLink}
              target="_blank"
              rel="noreferrer"
              className="text-center px-1 py-6 text-xs tracking-wide [writing-mode:vertical-rl] transform rotate-180 block"
            >
              <Typography variant="detail" className="text-neutral-400">
                Powered by AiChat
              </Typography>
            </a>
          </div>
        ) : (
          <div className="absolute left-[50%] bottom-4 -translate-x-[50%] bg-transparent">
            <a
              href={footerLink}
              target="_blank"
              rel="noreferrer"
              className="block"
            >
              <Typography
                className="text-center text-neutral-300 text-xs"
                variant="detail"
              >
                Powered by AiChat
              </Typography>
            </a>
          </div>
        )}
        <div className="bg-background-50 p-2 rounded-[24px] flex flex-col h-full shadow-xl border bg-gradient-to-t from-neutral-100 from-0% to-white/90 to-100%">
          {children}
        </div>
      </div>
      {/* hide minimize icon if isAlwaysOpen true */}
      {isAlwaysOpen ? null : (
        <Button
          size="icon"
          disabled={preventClose}
          className={cn(
            "absolute z-[999] size-7 bg-background/50 hover:bg-background rounded-full right-6",
            isOnMobile
              ? isFirstChat && !isVoiceMode
                ? "top-[9rem]"
                : "top-3"
              : "top-11",
          )}
          onClick={toggleChat}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
      {(!isFirstChat || isVoiceMode) && (
        <div
          id="sleek-not-first-chat-avatar"
          className={cn(
            "flex gap-2 items-center absolute",
            isOnMobile ? "top-3 left-6" : "top-11 left-14",
          )}
        >
          <AssistantAvatar
            avatarType="bot"
            variant={config.avatarType}
            customUrlClassName="size-7"
            className="size-7"
            customUrl={config.avatarUrl}
          />
          <Typography
            variant="small"
            className={cn(
              "font-bold",
              config.textColor === "light" ? "text-white" : "text-black",
            )}
          >
            {config.chatbotDisplayName}
          </Typography>
        </div>
      )}
    </>
  );
};

export default ThemeSleek;
